import { UseFormReturn, Controller } from 'react-hook-form';
import { SurveyFormData } from '@/utils/validation';
import type { Rating } from '@/types';

interface RatingInputProps {
  form: UseFormReturn<SurveyFormData>;
  name: keyof SurveyFormData['ratings'];
  label: string;
  error?: string;
}

const RatingInput = ({ form, name, label, error }: RatingInputProps) => {
  const { control } = form;

  const ratings: Rating[] = [1, 2, 3, 4, 5];
  const ratingLabels = {
    1: 'Very Poor',
    2: 'Poor',
    3: 'Fair',
    4: 'Good',
    5: 'Excellent',
  };

  const getRatingColor = (rating: Rating, selectedRating?: Rating) => {
    if (!selectedRating || rating > selectedRating) {
      return 'text-gray-300 hover:text-gray-400';
    }
    if (rating <= 2) return 'text-red-400';
    if (rating === 3) return 'text-yellow-400';
    return 'text-green-500';
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <label className="block text-sm font-semibold text-gray-700 mb-3">
        {label}
      </label>
      <Controller
        name={`ratings.${name}`}
        control={control}
        render={({ field }) => (
          <div className="space-y-3">
            <div className="flex items-center justify-between" role="radiogroup" aria-label={label}>
              <div className="flex space-x-1">
                {ratings.map((rating) => (
                  <label
                    key={rating}
                    htmlFor={`${name}-${rating}`}
                    className={`cursor-pointer transition-all transform hover:scale-110 ${
                      field.value === rating ? 'scale-110' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      id={`${name}-${rating}`}
                      value={rating}
                      checked={field.value === rating}
                      onChange={() => field.onChange(rating)}
                      className="sr-only"
                      aria-describedby={
                        error ? `${name}-error` : `${name}-${rating}-label`
                      }
                    />
                    <div
                      className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg transition-all ${
                        field.value === rating
                          ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-md'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-primary-300 hover:bg-primary-50'
                      } ${getRatingColor(rating, field.value as Rating)}`}
                    >
                      {rating}
                    </div>
                    <span
                      id={`${name}-${rating}-label`}
                      className="sr-only"
                    >
                      {rating} - {ratingLabels[rating]}
                    </span>
                  </label>
                ))}
              </div>
              {field.value && (
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">
                    {ratingLabels[field.value as Rating]}
                  </span>
                </div>
              )}
            </div>
            <div className="flex justify-between text-xs text-gray-500 px-1">
              <span>Very Poor</span>
              <span>Excellent</span>
            </div>
          </div>
        )}
      />
      {error && (
        <p id={`${name}-error`} className="mt-2 text-sm text-red-600 flex items-center" role="alert">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default RatingInput;

